import type { Editor } from '@tiptap/react';

import type { Label } from '../../../labels/labelTypes';
import { TiptapNodeType } from '../../tiptapTypes';
import { updateTaskItemAttrs } from './taskListItemAttrUtils';
import type { TaskItemLabel } from './taskListItemChipTypes';

/**
 * Converts a global label into the denormalised snapshot stored on a task item.
 */
export const createTaskItemLabelSnapshot = (label: Label): TaskItemLabel => ({
    labelId: label._id,
    name: label.name,
    colour: label.color,
});

/**
 * Reads the labels attr from a task item, defaulting to an empty list.
 */
export const getTaskItemLabels = (attrs: Record<string, unknown> | undefined): TaskItemLabel[] =>
    (attrs?.labels as TaskItemLabel[] | undefined) ?? [];

/**
 * Adds a label snapshot unless that label is already present.
 */
export const addTaskItemLabel = (labels: TaskItemLabel[], label: TaskItemLabel): TaskItemLabel[] =>
    labels.some(({ labelId }) => labelId === label.labelId) ? labels : [...labels, label];

/**
 * Removes a label by id.
 */
export const removeTaskItemLabel = (labels: TaskItemLabel[], labelId: string): TaskItemLabel[] =>
    labels.filter((label) => label.labelId !== labelId);

/**
 * Adds or removes a label by id.
 */
export const toggleTaskItemLabel = (labels: TaskItemLabel[], label: TaskItemLabel): TaskItemLabel[] =>
    labels.some(({ labelId }) => labelId === label.labelId)
        ? removeTaskItemLabel(labels, label.labelId)
        : addTaskItemLabel(labels, label);

/**
 * Updates the labels attr on the task item at the exact supplied ProseMirror position.
 */
export const setTaskItemLabels = ({
    editor,
    position,
    labels,
}: {
    editor: Editor;
    position: number;
    labels: TaskItemLabel[];
}): TaskItemLabel[] | undefined => {
    const attrs = updateTaskItemAttrs({ editor, position, update: () => ({ labels }) });
    return attrs?.labels as TaskItemLabel[] | undefined;
};

/**
 * Finds the exact document position of the task item with the supplied stable task ID.
 */
export const findTaskItemPositionById = ({
    editor,
    taskId,
}: {
    editor: Editor;
    taskId: string;
}): number | undefined => {
    let taskPosition: number | undefined;

    editor.state.doc.descendants((node, position) => {
        if (taskPosition !== undefined) return false;
        if (node.type.name !== TiptapNodeType.taskItem || node.attrs.taskId !== taskId) return true;

        taskPosition = position;
        return false;
    });

    return taskPosition;
};

/**
 * Removes a label from the task item identified by its stable task ID.
 */
export const removeTaskItemLabelByTaskId = ({
    editor,
    taskId,
    labelId,
}: {
    editor: Editor;
    taskId: string;
    labelId: string;
}): boolean => {
    const position = findTaskItemPositionById({ editor, taskId });
    if (position === undefined) return false;

    const labels = getTaskItemLabels(editor.state.doc.nodeAt(position)?.attrs);
    if (!labels.some((label) => label.labelId === labelId)) return false;

    return setTaskItemLabels({ editor, position, labels: removeTaskItemLabel(labels, labelId) }) !== undefined;
};
